<template>
  <SFooter
    :menuItems="getStrappiCoreMenu(menuItems)"
    :isShownPrivacyPreferences="getIsShownPrivacyPreferences"
    @openCookieDialog="openCookieDialog"
  >
    <template #customTopImages>
      <div class="position-absolute img-container flex-wrap">
        <div
          class="img-wrapper left overflow-hidden"
          :class="{
            'white': getIsCheckoutPage && !getIsMobile,
            'grey': !getIsCheckoutPage && !getIsMobile,
          }"
        >
          <img
            :src="require(`@/assets/img/footer-img-left${getIsMobile ? '-xs' : ''}.svg`)"
            :class="{ 'mobile': getIsMobile }"
            :alt="$t('altText.footerDecorationLeft')"
          >
        </div>
        <div
          v-if="!getIsMobile"
          class="middle-block"
        />
        <div
          v-if="!getIsMobile"
          class="img-wrapper right"
          :class="{'mobile': getIsMobile, 'white': getIsCheckoutPage && !getIsMobile, 'grey': !getIsCheckoutPage && !getIsMobile}"
        >
          <img
            :src="require('@/assets/img/footer-img-right.svg')"
            :alt="$t('altText.footerDecorationRight')"
          >
        </div>
      </div>
    </template>
    <template #customSocial>
      <a
        v-for="(item, key) in getStrappiCoreSettings.social"
        :key="key"
        :href="normalizeExternalHref(item.link)"
        :class="getStrappiCoreSettings.social.length - 1 !== key ? 'pr-6' : ''"
        target="_blank"
      >
        <img
          :class="getStrappiCoreSettings.social.length - 1 ? 'mr-6' : ''"
          :src="item.image && item.image.url"
          :alt="$t('altText.socialMediaLogo')"
        >
      </a>
    </template>
    <template #imgLeft>
      <img
        v-if="getStrappiCoreSettings.footerImgLeft"
        :src="getStrappiCoreSettings.footerImgLeft.url"
        :alt="$t('altText.footerLogoLeft')"
      >
    </template>
    <template #imgRight>
      <img
        v-if="getStrappiCoreSettings.footerImgRight"
        :src="getStrappiCoreSettings.footerImgRight.url"
        :alt="$t('altText.footerLogoRight')"
      >
    </template>
    <template #rights>
      {{ getStrappiCoreSettings.footerRights }}
    </template>
    <template #bottomLeft>
      <VueMarkdown
        v-if="footerBottomLeft"
      >
        {{ footerBottomLeft }}
      </VueMarkdown>
    </template>
    <template #bottomRight>
      <VueMarkdown
        v-if="footerBottomRight"
      >
        {{ footerBottomRight }}
      </VueMarkdown>
    </template>
  </SFooter>
</template>

<script lang="ts">
export default {
  name: 'Footer',
};
</script>

<script setup lang="ts">
import { computed, watch } from 'vue';
import {
  SFooter, useStore, useRouteRef, useVuetify, useOpenLinkNewTab,
} from '@slg/web-customer-shared';
import { normalizeExternalHref, normalizeExternalLinksInText } from '@slg/web-customer-shared/src/utils/externalLinks';
import appConfig from '@/appConfig';
import VueMarkdown from 'vue-markdown';

defineProps({
  isFooterFullyShow: {
    type: Boolean,
    default: true,
  },
});

const store = useStore();
const route = useRouteRef();
const vuetify = useVuetify();

const { setTargetAttributeToLink } = useOpenLinkNewTab();
const getStrappiCoreMenu = computed(() => store.getters['common/getStrappiCoreMenu']);
const getStrappiCoreSettings = computed(() => store.getters['common/getStrappiCoreSettings']);
const footerBottomLeft = computed(() => (
  normalizeExternalLinksInText(getStrappiCoreSettings.value?.footerBottomLeft || '')
));
const footerBottomRight = computed(() => (
  normalizeExternalLinksInText(getStrappiCoreSettings.value?.footerBottomRight || '')
));

watch(
  () => [footerBottomLeft.value, footerBottomRight.value],
  () => {
    setTargetAttributeToLink(['footer-additional']);
  },
  { immediate: true },
);

const getIsShownPrivacyPreferences = computed(() => appConfig.extendedCookieSupport);
const getIsMobile = computed(() => vuetify.breakpoint.xs);
const getIsCheckoutPage = computed(() => route.value.name === 'Checkout');

const getJobPage = computed(() => {
  if (route.value.params.lang === 'de') {
    return 'https://www.slg.lu/de/career/';
  }
  if (route.value.params.lang === 'fr') {
    return 'https://www.slg.lu/career/';
  }
  return 'https://www.slg.lu/en/career/';
});

const getPrivacyPolicyPage = computed(() => {
  if (route.value.params.lang === 'de') {
    return 'https://www.slg.lu/de/politique-de-confidentialite/';
  }
  if (route.value.params.lang === 'fr') {
    return 'https://www.slg.lu/politique-de-confidentialite/';
  }
  return 'https://www.slg.lu/en/politique-de-confidentialite/';
});

const menuItems = computed(() => [
  {
    strapiText: '',
    strapiKey: 'homeTitle',
    isAnotherDomain: false,
    title: 'footer.home',
    path: '/',
  },
  {
    title: 'footer.about',
    isAnotherDomain: false,
    path: { name: 'About' },
  },
  {
    title: 'footer.lost&found',
    isAnotherDomain: false,
    path: { name: 'ContactUs' },
  },
  {
    strapiText: '',
    isAnotherDomain: false,
    strapiKey: 'FAQTitle',
    title: 'footer.faq',
    path: { name: 'FAQ' },
  },
  {
    strapiText: '',
    isAnotherDomain: false,
    strapiKey: 'contactUsTitle',
    title: 'footer.contactUs',
    path: { name: 'ContactUs' },
  },
  {
    title: 'footer.jobs',
    isAnotherDomain: true,
    path: { name: getJobPage.value },
  },
  {
    strapiText: '',
    strapiKey: 'termsTitle',
    isAnotherDomain: false,
    title: 'footer.t&c',
    path: { name: 'TermsAndConditions' },
  },
  {
    strapiText: '',
    isAnotherDomain: false,
    strapiKey: 'sitemapTitle',
    title: 'footer.sitemap',
    path: { name: 'Sitemap' },
  },
  {
    strapiText: '',
    isAnotherDomain: true,
    strapiKey: 'policyTitle',
    title: 'footer.privacyPolicy',
    path: { name: getPrivacyPolicyPage.value },
  },
]);

function openCookieDialog() {
  store.commit('common/changeCookieDialogOpenState', true);
}
</script>
<style lang="scss" scoped>
.img-container {
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  z-index: 1;

  .img-wrapper {
    position: absolute;
    max-width: 100%;
    z-index: 1;

    &.mobile {
      background-color: unset;
    }

    &.grey {
      background-color: var(--v-pageBg-base) !important;
    }

    &.white {
      background-color: var(--v-cardBg-base);
    }

    &.left {
      left: 0;
    }

    &.right {
      right: 0;
    }

    img {
      max-width: 100%;

       &.mobile {
        max-width: unset;
      }
    }
  }

  .middle-block {
    position: absolute;
    max-width: 100%;
    right: 0;
    width: 100%;
    height: 27px;
    z-index: 0;
    background-color: #424242;
  }
}
</style>
